/**
 * List colours, and the mapping off the old set.
 *
 * A direct port of taskdojo-app/src/constants/listColors.js. Keep the two in
 * step: a list stores its colours literally rather than by id, so the two
 * platforms must agree on both the palette and the legacy mapping or the same
 * list renders in different colours depending on where you open it.
 *
 * The originals were stock gradients from before the app had a palette -- hot
 * pink, electric cyan, purple-to-magenta -- and on a list card they shouted
 * over everything around them. Each pair is now two steps of one hue rather
 * than two different hues: a gradient crossing from pink to yellow reads as
 * decoration, one that deepens within a colour reads as a surface, which is
 * what a card is.
 *
 * Because the value is stored, replacing the options alone would only affect
 * new lists. LEGACY maps the old values onto the new ones by position, applied
 * at render, so existing lists re-skin themselves without a data migration and
 * without anyone re-picking a colour. A list whose colours are not recognised
 * is left exactly as it is -- better an unexpected colour than a wrong one.
 */
export interface ListColorOption {
  id: string;
  colors: [string, string];
}

export const LIST_COLORS: ListColorOption[] = [
  { id: '1',  colors: ['#4A6FA5', '#3A5680'] },  // blue
  { id: '2',  colors: ['#E34234', '#A82D22'] },  // red
  { id: '3',  colors: ['#3E8C84', '#2C6862'] },  // teal
  { id: '4',  colors: ['#4C9A2A', '#37701E'] },  // green
  { id: '5',  colors: ['#C1763C', '#96562A'] },  // orange
  { id: '6',  colors: ['#8E5572', '#6B3F56'] },  // plum
  { id: '7',  colors: ['#C08A1E', '#8E6414'] },  // gold
  { id: '8',  colors: ['#5B6E7F', '#42525F'] },  // slate
  { id: '9',  colors: ['#7A5C9E', '#5B4477'] },  // violet
  { id: '10', colors: ['#2F7A8C', '#215A68'] },  // deep cyan
  { id: '11', colors: ['#B0553F', '#853E2D'] },  // clay
  { id: '12', colors: ['#4F7A5B', '#3A5B44'] },  // moss
];

/** Old first-stop -> new pair, by the slot it used to occupy. */
const LEGACY: Record<string, number> = {
  '#4a6fa5': 0, '#c1763c': 4, '#43e97b': 3, '#fa709a': 5,
  '#30cfd0': 9, '#e34234': 1, '#fd297b': 5, '#9f44d3': 8,
  '#00d2ff': 9, '#fe8c00': 10, '#38ef7d': 11, '#00f2fe': 2,
};

export const DEFAULT_LIST_COLORS = LIST_COLORS[0].colors;

/**
 * Resolve whatever a list has stored into a pair to draw with.
 * Accepts an array, a single colour string, or nothing.
 */
export function resolveListColors(stored: unknown): [string, string] {
  if (!stored) return DEFAULT_LIST_COLORS;

  const pair = Array.isArray(stored)
    ? (stored as string[])
    : (typeof stored === 'string' ? [stored, stored] : null);
  if (!pair || !pair.length) return DEFAULT_LIST_COLORS;

  // Already one of ours: leave it alone.
  const current = LIST_COLORS.find(
    (o) => o.colors[0].toLowerCase() === String(pair[0]).toLowerCase(),
  );
  if (current) return current.colors;

  const slot = LEGACY[String(pair[0]).toLowerCase()];
  if (slot !== undefined) return LIST_COLORS[slot].colors;

  // Unrecognised -- a custom colour, or one we have not seen. Keep it.
  return pair.length >= 2 ? [pair[0], pair[1]] : [pair[0], pair[0]];
}
